import { BaseDataListResponse } from 'Base'
import { RegistrationHistoryData } from 'Models'
import axiosInstant from 'api/baseRequest'
import ListView from 'components/BaseManagementComponent/ListView'
import FilterSelect from 'components/InfiniteSelect/FilterSelect'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { FilterMatchMode } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column, ColumnProps } from 'primereact/column'
import { ConfirmDialogProps } from 'primereact/confirmdialog'
import { DataTable, DataTableFilterMetaData } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { Paginator } from 'primereact/paginator'
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { WorkspaceContext } from 'context/workspace.context'
import { DATE_TIME_FORMAT } from 'utils/constants/datetime'
import { EventStatusEnum, RegistrationStatusEnum } from 'utils/constants/event'
import { UserRoleEnum } from 'utils/constants/user'
import { getEventStatusTag, getRegistrationStatusTag } from 'utils/helper/table'
import { useIsMobile } from 'utils/hooks/useIsMobile'

export interface ActionColumn {
  key?: string
  icon?: ReactNode
  showConfirm?: ConfirmDialogProps
  tooltip?: ReactNode
  href?: (data?: any) => string
  action?: (data?: any) => void
  disabled?: (data?: any) => boolean
}

export interface BaseManagemenColumnProps extends ColumnProps {
  dataIndex?: string
  key?: string
  filterKey?: string
  width?: any
}
export interface IUserRegistrationHistoryProps {
  userId?: string
}
const UserRegistrationHistory = ({ userId }: IUserRegistrationHistoryProps) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { user } = useContext(WorkspaceContext)
  const isHeadquarterAdmin = user?.role === UserRoleEnum.Headquarter
  const userBranchId = user?.branch?._id
  const [showMobileFilter, setShowMobileFilter] = useState(false)
  const [mobileEventStatus, setMobileEventStatus] = useState<string | null>(
    null
  )
  const [mobileReservationStatus, setMobileReservationStatus] = useState<
    string | null
  >(null)
  const [filterData, setFilterData] = useState<Record<string, any>>({
    page: 1,
    sort: null,
    filter: null,
    sortBy: null,
  })
  const [loading, setLoading] = useState(false)
  const [dataList, setDataList] = useState<
    BaseDataListResponse<RegistrationHistoryData>
  >({
    meta: {},
    data: [],
  })

  const renderEventTitle = useCallback(
    (item: RegistrationHistoryData) => {
      const event = item.event?.[0] as
        | (RegistrationHistoryData['event'][number] & { deleted?: boolean })
        | undefined
      const branchId = (item as RegistrationHistoryData & { branchId?: string })
        .branchId
      const title = event?.title ?? '-'
      const canOpenEvent =
        isHeadquarterAdmin || (!!userBranchId && userBranchId === branchId)

      if (!canOpenEvent || event?.deleted || !branchId || !event?._id) {
        return title
      }

      return (
        <a
          target="_blank"
          rel="noreferrer"
          className="text-blue-500 underline"
          href={`/${branchId}/event-management/${event._id}`}
        >
          {title}
        </a>
      )
    },
    [isHeadquarterAdmin, userBranchId]
  )

  const columns = useMemo<BaseManagemenColumnProps[]>(() => {
    return [
      {
        key: 'event',
        dataIndex: 'event',
        header: t('Event name'),
        body: (item) => renderEventTitle(item),
        width: '30%',
      },
      {
        key: 'branchName',
        dataIndex: 'branchName',
        header: t('Branch name'),
        body: (item) => item.branchName ?? '-',
        width: '15%',
      },
      {
        key: 'location',
        dataIndex: 'location',
        header: t('Location'),

        body: (item, d) => item.event?.[0].location,
        width: '25%',
      },
      {
        key: 'timeslot',
        dataIndex: 'timeslot',
        sortable: true,
        header: t('Time slot'),
        
        body: (data) =>
          `${dayjs(data.timeslot?.startTime).format(DATE_TIME_FORMAT.HOUR)}-${dayjs(data.timeslot?.endTime).format(DATE_TIME_FORMAT.HOUR)} ${data.event?.[0]?.startTimeEvent ? dayjs(data.event[0].startTimeEvent).format(DATE_TIME_FORMAT.DAY_ONLY) : ''}`,
        width: '240px',
      },
      {
        key: 'event-status',
        header: t('Status'),
        filter: true,
        filterElement: (options) => {
          return (
            <FilterSelect
              options={Object.values(EventStatusEnum).map((e) => ({
                label: t(e),
                value: e,
              }))}
              showSearch={false}
              value={options.value}
              onSelectItem={(_, value) => {
                options.filterCallback(value, options.index)
              }}
              onChange={(e) => {
                options.filterApplyCallback(e, options.index)
              }}
              placeholder={t('Select One')}
            />
          )
        },
        width: '120px',

        showFilterMatchModes: false,
        filterField: 'eventStatus',
        filterMatchMode: 'contains',
        body: (item, d) => getEventStatusTag(item.event?.[0].status),
      },
      {
        key: 'guest',
        dataIndex: 'guest',
        header: t('Guest'),
        width: '10%',
      },
      {
        key: 'status',
        dataIndex: 'status',
        header: t('Registration Status'),
        body: (item) => getRegistrationStatusTag(item.status),
        filter: true,
        filterElement: (options) => {
          return (
            <FilterSelect
              options={Object.values(RegistrationStatusEnum).map((e) => ({
                label: t(e),
                value: e,
              }))}
              showSearch={false}
              value={options.value}
              onSelectItem={(_, value) => {
                options.filterCallback(value, options.index)
              }}
              onChange={(e) => options.filterApplyCallback(e, options.index)}
              placeholder={t('Select One')}
            />
          )
        },
        filterApply: (options) => {
          return (
            <Button
              label={t('Apply')}
              onClick={options.filterApplyCallback}
            ></Button>
          )
        },
        filterClear: (options) => {
          return (
            <Button
              outlined
              label={t('Clear')}
              onClick={options.filterClearCallback}
            ></Button>
          )
        },
        showFilterMatchModes: false,
        filterField: 'reservationStatus',
        filterMatchMode: 'contains',
        width: '120px',
      },
    ]
  }, [renderEventTitle, t])

  const eventStatusOptions = useMemo(
    () =>
      Object.values(EventStatusEnum).map((e) => ({
        label: t(e),
        value: e,
      })),
    [t]
  )

  const reservationStatusOptions = useMemo(
    () =>
      Object.values(RegistrationStatusEnum).map((e) => ({
        label: t(e),
        value: e,
      })),
    [t]
  )

  const filters = useMemo(() => {
    const obj: any = {}
    columns
      .filter((col) => col.filter)
      .forEach((col) => {
        obj[col.filterField ?? col.key] = {
          value: filterData[col.filterField],
          matchMode: FilterMatchMode.CUSTOM,
        }
      })
    // Object.keys(searchObject).forEach((key) => {
    //   if (key !== 'page' && key !== 'perPage' && key !== 'sortBy') {
    //     obj[key] = {
    //       value: searchObject[key],
    //       matchMode: FilterMatchMode.CUSTOM,
    //     }
    //   }
    // })
    // console.log(obj)
    return obj
  }, [columns, filterData])

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await axiosInstant.request({
        url: `/api/v1/reservation/history/${userId}`,
        params: filterData,
      })
      setDataList(data)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }, [filterData, userId])

  const sortObject = useMemo<any>(() => {
    const sortArray = filterData.sortBy ? filterData.sortBy?.split(':') : []
    if (sortArray.length)
      return {
        sortField: sortArray[0]?.replaceAll('"', ''),
        sortOrder: Number(sortArray[1]),
      }
    else return {}
  }, [filterData.sortBy])

  useEffect(() => {
    getData()
  }, [getData])

  useEffect(() => {
    const es = filterData.eventStatus
    const rs = filterData.reservationStatus
    setMobileEventStatus(
      typeof es === 'string'
        ? es
        : Array.isArray(es) && es.length
          ? String(es[0])
          : null
    )
    setMobileReservationStatus(
      typeof rs === 'string'
        ? rs
        : Array.isArray(rs) && rs.length
          ? String(rs[0])
          : null
    )
  }, [filterData.eventStatus, filterData.reservationStatus])

  const handleFilterDataChange = (filter) => {
    setFilterData((prev) => ({ ...prev, ...filter }))
  }

  const formatHistoryTimeslot = (item: RegistrationHistoryData) =>
    `${dayjs(item.timeslot?.startTime).format(DATE_TIME_FORMAT.HOUR)}-${dayjs(item.timeslot?.endTime).format(DATE_TIME_FORMAT.HOUR)}${item.event?.[0]?.startTimeEvent ? ` ${dayjs(item.event[0].startTimeEvent).format(DATE_TIME_FORMAT.DAY_ONLY)}` : ''}`

  return (
    <div
      className={clsx(
        isMobile
          ? 'flex flex-column flex-1 h-full min-h-0 overflow-hidden p-3 gap-3'
          : 'card bg-white border-round-xl p-3 pb-6'
      )}
    >
      {isMobile ? (
        <>
          <Button
            type="button"
            icon="isax isax-filter-search"
            label={t('Filter')}
            className="w-full h-auto"
            severity="secondary"
            onClick={() => setShowMobileFilter(true)}
          />
          <Dialog
            visible={showMobileFilter}
            onHide={() => setShowMobileFilter(false)}
            draggable={false}
            resizable={false}
            modal
            header={t('Filter')}
            className="mobile-select-branch-dialog m-0 shadow-none border-noround"
            contentClassName="p-0 h-full"
            style={{
              width: '100vw',
              maxWidth: '100vw',
              height: '100vh',
              maxHeight: '100vh',
            }}
          >
            <div className="w-full h-full p-3 relative">
              <div className="card m-0 p-3 flex flex-column gap-3">
                <div>
                  <label className="font-semibold mb-2 block">
                    {t('Status')}
                  </label>
                  <FilterSelect
                    options={eventStatusOptions}
                    showSearch={false}
                    className="w-full"
                    multiple={false}
                    value={mobileEventStatus ? [mobileEventStatus] : []}
                    onSelectItem={(_, selectedValues) => {
                      setMobileEventStatus(
                        (selectedValues?.[0] as string) || null
                      )
                    }}
                    showFooter={false}
                    placeholder={t('Select One')}
                  />
                </div>
                <div>
                  <label className="font-semibold mb-2 block">
                    {t('Registration Status')}
                  </label>
                  <FilterSelect
                    options={reservationStatusOptions}
                    showSearch={false}
                    className="w-full"
                    multiple={false}
                    value={
                      mobileReservationStatus ? [mobileReservationStatus] : []
                    }
                    onSelectItem={(_, selectedValues) => {
                      setMobileReservationStatus(
                        (selectedValues?.[0] as string) || null
                      )
                    }}
                    showFooter={false}
                    placeholder={t('Select One')}
                  />
                </div>
              </div>
              <div className="fixed left-0 right-0 bottom-0 p-3 bg-white border-top-1 surface-border flex gap-3">
                <Button
                  type="button"
                  outlined
                  label={t('Clear')}
                  className="flex-1"
                  onClick={() => {
                    setMobileEventStatus(null)
                    setMobileReservationStatus(null)
                    handleFilterDataChange({
                      eventStatus: null,
                      reservationStatus: null,
                      page: 1,
                    })
                    setShowMobileFilter(false)
                  }}
                />
                <Button
                  type="button"
                  label={t('Apply')}
                  className="flex-1"
                  onClick={() => {
                    handleFilterDataChange({
                      eventStatus: mobileEventStatus,
                      reservationStatus: mobileReservationStatus,
                      page: 1,
                    })
                    setShowMobileFilter(false)
                  }}
                />
              </div>
            </div>
          </Dialog>
        </>
      ) : null}

      <div
        className={clsx(
          isMobile ? 'overflow-auto flex-1 min-h-0' : undefined,
          !isMobile && 'mt-3'
        )}
      >
        {isMobile ? (
          <ListView
            dataSource={dataList?.data}
            loading={loading}
            mobileListRender={(item) => (
              <div className="border-1 surface-border border-round-lg p-3 text-color hover:surface-50 transition-duration-150">
                <div className="flex justify-content-between align-items-start gap-2 mb-2 flex-wrap">
                  <div className="font-semibold text-lg line-height-3 flex-1 min-w-0">
                    {renderEventTitle(item)}
                  </div>
                  <div className="flex flex-wrap gap-2 align-items-center justify-content-end flex-shrink-0">
                    {item.event?.[0]
                      ? getEventStatusTag(item.event[0].status)
                      : null}
                    {getRegistrationStatusTag(item.status)}
                  </div>
                </div>
                <div className="text-sm text-color-secondary mb-2 white-space-normal">
                  <i className="isax-location mr-2" />
                  {item.event?.[0]?.location ?? '-'}
                </div>
                <div className="text-sm mb-2 white-space-normal">
                  <span className="font-medium mr-2">{t('Branch name')}:</span>
                  {item.branchName ?? '-'}
                </div>
                <div className="text-sm mb-2 white-space-normal">
                  {formatHistoryTimeslot(item)}
                </div>
                <div className="text-sm white-space-normal">
                  <span className="font-medium mr-2">{t('Guest')}:</span>
                  {item.guest}
                </div>
              </div>
            )}
          />
        ) : (
          <DataTable
            value={dataList?.data}
            className="mt-0"
            removableSort
            loading={loading}
            emptyMessage={t('No available options')}
            filters={filters}
            loadingIcon={
              <div className="absolute w-full h-full">
                <div className="skeleton-container">
                  <div className="skeleton-placeholder skeleton-title"></div>
                  <div className="skeleton-placeholder skeleton-content"></div>
                  <div className="skeleton-placeholder skeleton-content"></div>
                  <div className="skeleton-placeholder skeleton-content"></div>
                  <div className="skeleton-placeholder skeleton-content"></div>
                  <div className="skeleton-placeholder skeleton-content"></div>
                </div>
              </div>
            }
            onFilter={(e) => {
              const valueObject = {}

              Object.keys(e.filters).forEach((key) => {
                valueObject[key] = (
                  e.filters[key] as DataTableFilterMetaData
                )?.value
              })
              handleFilterDataChange(valueObject)
            }}
            lazy
            {...sortObject}
            onSort={(e) => {
              handleFilterDataChange({
                sortBy: e.sortOrder ? `"${e.sortField}":${e.sortOrder}` : null,
              })
            }}
          >
            {columns.map((col) => (
              <Column
                {...col}
                field={col.dataIndex ?? col.key}
                style={
                  col.width
                    ? {
                        width: col.width,
                      }
                    : null
                }
                key={col.key}
                filterApply={(options) => {
                  return (
                    <Button
                      label={t('Apply')}
                      onClick={options.filterApplyCallback}
                    ></Button>
                  )
                }}
                filterClear={(options) => {
                  return (
                    <Button
                      outlined
                      label={t('Clear')}
                      onClick={options.filterClearCallback}
                    ></Button>
                  )
                }}
              ></Column>
            ))}
          </DataTable>
        )}
      </div>
      {!loading && dataList && dataList.meta?.total && dataList.meta?.page ? (
        <Paginator
          first={(dataList.meta.page - 1) * dataList.meta.perPage}
          rows={dataList.meta.perPage}
          totalRecords={dataList.meta.total}
          template={{
            layout: isMobile
              ? 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink'
              : 'CurrentPageReport FirstPageLink PrevPageLink  PageLinks NextPageLink LastPageLink RowsPerPageDropdown',
            CurrentPageReport: (options) => {
              return (
                <span>
                  {options.first} - {options.last} of {options.totalRecords}
                </span>
              )
            },
          }}
          rowsPerPageOptions={[10, 20, 50]}
          onPageChange={(e) =>
            handleFilterDataChange({
              page: e.page + 1,
              perPage: e.rows,
            })
          }
          className={clsx(
            'justify-content-center base-management-paginator',
            isMobile && 'mt-2 flex-shrink-0'
          )}
        />
      ) : null}
    </div>
  )
}
export default UserRegistrationHistory
